import React, { useCallback, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'semantic-ui-react';
import styles from '../../../../containers/ReportClaimPage.module.scss';
import states from '../../../States';
import FormPhoneNumberField from './FormPhoneNumberField';
import { animateScroll as scroll } from 'react-scroll';

const AddThirdPartyDriverModal = ({ onAddDriver, policyNumber }) => {
	const defaultDriver = useMemo(() => {
		return {
			firstName: '',
			middleName: '',
			lastName: '',
			homePhone: '',
			businessPhone: '',
			cellPhone: '',
			fax: '',
			email: '',
			streetNumber: '',
			streetName: '',
			apartmentNumber: '',
			poBox: '',
			city: '',
			stateId: null,
			state: '',
			zip: '',
		};
	}, []);

	const [driver, setDriver] = useState(defaultDriver);
	const [fileList, setFileList] = useState([]);
	const onChange = useCallback((_, { name, value }) => {
		setDriver({ ...driver, [name]: value });
	}, [driver])

	const stateOptions = useMemo(
		() =>
			states.map((s, i) => {
				return {
					text: `${s.abbreviation}`,
					value: s.id,
					key: i,
				};
			}),
		[]
	);
	const fileDelete = useCallback(
		async (file, index) => {
			var newList = fileList.map((x) => x);
			newList.splice(index, 1);
			setFileList(newList);

			// const combinedFileSize = newList.map(item => item.size).reduce((prev, curr) => prev + curr, 0);
			// setAggregatedAttachmentSize(combinedFileSize);
		},
		[fileList]
	);
	const files = fileList.map((file, index) => (
		<li key={file.path}>
			{file.path} - {file.size} bytes <Button icon='trash' title='Remove' className={styles.trashButton} onClick={() => fileDelete(file, index)} />
		</li>
	));
	const [fieldErrors, setFieldErrors] = useState({});
	const fieldValidations = useMemo(() => {
		return {
			homePhone: (d) => d.homePhone.trim() !== '' || d.homePhone.trim() !== '' || d.homePhone.trim() !== '',
			businessPhone: (d) => d.businessPhone.trim() !== '' || d.businessPhone.trim() !== '' || d.businessPhone.trim() !== '',
			cellPhone: (d) => d.cellPhone.trim() !== '' || d.cellPhone.trim() !== '' || d.cellPhone.trim() !== ''
		};
	}, [defaultDriver, driver]);

	const onAdd = useCallback(() => {
		if (driver.stateId) {
			driver.state = states.find(x => x.id === driver.stateId).name;
		}
		const validationKeys = Object.keys(fieldValidations);
		let focusField = '';
		const newErrors = {};
		validationKeys.forEach((key, i) => {
			var validation = fieldValidations[key];
			if (['cellPhone', 'homePhone', 'businessPhone'].includes(key) && driver[key]) {
				if (!/^\d{10}$/.test(driver[key])) {
					newErrors[key] = 'Invalid phone number';
				}
			}
			if (newErrors[key] && !focusField) {
				focusField = key;
			}
		});
		if (focusField) {
			let focusElement = document.querySelector(`[name="${focusField}"]`);

			if (focusElement) {
				if (focusElement.nodeName === 'DIV') {
					focusElement = document.querySelector(`[id="${focusField}_FocusAnchor"]`);
				}

				if (focusElement) {
					if (navigator.userAgent.indexOf('Firefox') > -1) {
						scroll.scrollMore(focusElement.getBoundingClientRect().top - 250, { smooth: true });
					}

					focusElement.focus();
				}
			}
			setFieldErrors(newErrors);
			return;
		}


		onAddDriver(driver);
		setDriver({ ...defaultDriver });
		setOpen(false);
		setFieldErrors({});
	}, [fieldValidations, defaultDriver, driver, onAddDriver])

	const onCancel = useCallback(() => {
		setDriver({ ...defaultDriver });
		setOpen(false);
	}, [defaultDriver])

	const [open, setOpen] = useState(false);

	return (
		<Modal open={open} onOpen={() => setOpen(true)} onClose={() => setOpen(false)} trigger={<Button id="addThirdPartyDriverBtn" primary>Add Third Party Driver</Button>}>
			<Modal.Header>Add Third Party Driver</Modal.Header>
			<Modal.Content>
				<Form>
					<div className={styles.row}>
						<Form.Input name='firstName' label='First Name' onChange={onChange} value={driver.firstName} />
						<Form.Input name='middleName' label='Middle Name' onChange={onChange} value={driver.middleName} />
						<Form.Input name='lastName' label='Last Name' onChange={onChange} value={driver.lastName} />
					</div>

					{['PPA', 'CAP'].includes(policyNumber.substring(0, 3)) && (
						<div className={styles.row}>
							<FormPhoneNumberField name='cellPhone' label='Cell Phone' onChange={onChange} value={driver.cellPhone} type="number" error={fieldErrors['cellPhone']} />
							<FormPhoneNumberField name='homePhone' label='Home Phone' onChange={onChange} value={driver.homePhone} type="number" error={fieldErrors['homePhone']} />
							<FormPhoneNumberField name='businessPhone' label='Business Phone' onChange={onChange} value={driver.businessPhone} type="number" error={fieldErrors['businessPhone']} />
						</div>
					)}
					{!['PPA', 'CAP'].includes(policyNumber.substring(0, 3)) && (
						<div className={styles.row}>
							<FormPhoneNumberField name='homePhone' label='Home Phone' onChange={onChange} value={driver.homePhone} type="number"  error={fieldErrors['homePhone']} />
							<FormPhoneNumberField name='cellPhone' label='Cell Phone' onChange={onChange} value={driver.cellPhone} type="number" error={fieldErrors['cellPhone']} />
							<FormPhoneNumberField name='businessPhone' label='Business Phone' onChange={onChange} value={driver.businessPhone} type="number" error={fieldErrors['businessPhone']} />
						</div>
					)}

					<div className={styles.row}>
						<FormPhoneNumberField name='fax' label='Fax' onChange={onChange} value={driver.fax} type="number" />
						<Form.Input name='email' label='Email' onChange={onChange} value={driver.email} type="email" />
					</div>
					<div className={styles.row}>
						<Form.Input name='streetNumber' label='Street #' onChange={onChange} value={driver.streetNumber} type="number" />
						<Form.Input name='streetName' label='Street' onChange={onChange} value={driver.streetName} />
					</div>
					<div className={styles.row}>
						<Form.Input name='apartmentNumber' label='Apartment #' onChange={onChange} value={driver.apartmentNumber} type="number" />
						<Form.Input name='poBox' label='P.O. Box' onChange={onChange} value={driver.poBox} type="number" />
					</div>
					<div className={styles.row}>
						<Form.Input name='city' label='City' onChange={onChange} value={driver.city} />
						<Form.Dropdown id="stateDropDown" name='stateId' label='State' selection options={stateOptions} onChange={onChange} value={driver.stateId} />
					</div>
					<div className={styles.row}>
						<Form.Input name='zip' label='Zip' onChange={onChange} value={driver.zip} type="number" />
					</div>
				</Form>
			</Modal.Content>
			<Modal.Actions>
				<Button id="cancelBtn" onClick={onCancel} primary>Cancel</Button>
				<Button id="addBtn" onClick={onAdd} primary>Add</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default AddThirdPartyDriverModal;
